import DOMPurify from 'dompurify';

/**
 * Represents data that can be safely sanitized.
 * Supports primitives, arrays, and plain objects.
 */
type SanitizableData =
  | string
  | number
  | boolean
  | null
  | undefined
  | SanitizableData[]
  | { [key: string]: SanitizableData };

/**
 * Recursively sanitizes data to prevent XSS attacks.
 * - Strings are sanitized using DOMPurify
 * - Objects and arrays are recursively processed
 * - Numbers and booleans are returned as-is
 *
 * @param data The data to sanitize
 * @returns Sanitized data safe for rendering
 */
export const sanitizeData = (data: SanitizableData): SanitizableData => {
  if (typeof data !== 'boolean' && !data) {
    return null;
  }
  // if data is an array, recursively sanitize it
  if (Array.isArray(data)) {
    return data.map((eachValue): SanitizableData => {
      return sanitizeData(eachValue);
    });
  }
  // if data is an object, recursively clean the values of each key
  if (typeof data === 'object') {
    return Object.keys(data).reduce<{ [key: string]: SanitizableData }>((acc, eachKey) => {
      acc[eachKey] = sanitizeData(data[eachKey]);
      return acc;
    }, {});
  }
  // if it's a boolean or number, no need to sanitize
  if (typeof data === 'boolean' || typeof data === 'number') {
    return data;
  }

  return DOMPurify.sanitize(data);
};

